import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { useDashboard } from '../useDashboard'

interface MiniTileProps {
	label: string
	value: number | undefined
	/** Hover/long-press explanation. Surfaces what the counter actually measures. */
	tooltip: string
	/** Optional deep link — renders the tile as a clickable card when set. */
	href?: string
}

function MiniTile({ label, value, tooltip, href }: MiniTileProps) {
	const body = (
		<div
			className={`rounded-md bg-gray-50 p-3 text-center ${
				href ? 'transition-colors hover:bg-gray-100 focus-within:bg-gray-100' : ''
			}`}
			title={tooltip}
		>
			<p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
			<p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
		</div>
	)
	if (!href) return body
	return (
		<Link
			to={href}
			aria-label={`${label} — ${tooltip}`}
			className="block focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-md"
		>
			{body}
		</Link>
	)
}

/**
 * Admin-only "Admin Snapshot" widget — surfaces the operational pulse of
 * the Request Center via the four counters from `GET /requests/stats`:
 *   - totalPending      : pending approval rows (any owner)
 *   - provisioning      : requests in `RequestState.APPROVED` (post-approval,
 *                         ticket lifecycle pending)
 *   - stale             : non-final requests submitted > 7 days ago
 *   - productiveErrors  : requests whose `metadata.productiveError` is set
 *
 * `Total pending` deep-links into the admin-scope approval inbox
 * (`/requests-center/approvals?scope=all`). The other three are tooltips
 * only for now — there is no admin-wide request list yet, and a broken
 * link is worse than a hover-explanation. Tracked: build a filtered
 * admin-requests view so Provisioning / Stale / Productive errors can
 * deep-link to a real query (each maps cleanly to a single backend filter).
 *
 * Self-gates on `isAdminViewer` so non-admin USERs render nothing AND the
 * underlying admin-stats query never fires (per `useDashboard`'s
 * `enabled: admin` flag).
 *
 * Spec 4 / Group 9f — `request-center-dashboard.md`.
 */
export function AdminSnapshotWidget() {
	const { isAdminViewer, adminStatsQuery } = useDashboard()
	if (!isAdminViewer) return null

	if (adminStatsQuery.isError) {
		return (
			<Card className="p-4">
				<h2 className="mb-3 text-sm font-semibold text-gray-700">Admin Snapshot</h2>
				<p className="text-sm text-red-700">Couldn't load Admin Snapshot. Try refreshing the page.</p>
			</Card>
		)
	}

	const stats = adminStatsQuery.data
	return (
		<Card className="p-4">
			<h2 className="mb-3 text-sm font-semibold text-gray-700">Admin Snapshot</h2>
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
				<MiniTile
					label="Total pending"
					value={stats?.totalPending}
					tooltip="Approval rows currently awaiting a decision (any approver, any request). Click to open the all-approvals inbox."
					href="/requests-center/approvals?scope=all"
				/>
				<MiniTile
					label="Provisioning"
					value={stats?.provisioning}
					tooltip="Requests past the approval gate but waiting for ticket-lifecycle execution to complete (state = approved)."
					href="/requests-center/admin/requests?state=approved"
				/>
				<MiniTile
					label="Stale"
					value={stats?.stale}
					tooltip="Non-final requests submitted more than 7 days ago — likely stuck in the queue and worth investigating."
					href="/requests-center/admin/requests?staleOnly=true"
				/>
				<MiniTile
					label="Productive errors"
					value={stats?.productiveErrors}
					tooltip="Requests whose post-approval Productive ticket creation failed. metadata.productiveError holds the cause."
					href="/requests-center/admin/requests?productiveErrorsOnly=true"
				/>
			</div>
		</Card>
	)
}
